/**
 * Overflow-driven paginated document renderer.
 *
 * Takes an HTML string and renders it across N fixed-size `.corner-page` divs
 * by walking logical content blocks, appending each into the current page,
 * and rolling back on overflow. Splits paragraphs at sentence -> word
 * boundaries when necessary; never splits mid-word.
 *
 * Pagination is a pure render concern: every render recomputes from the
 * current `html` prop. Nothing is persisted. Page breaks change as font,
 * viewport, zoom, or page size change.
 *
 * For streaming AI drafts: pass `streaming=true` and the layout pass is
 * debounced so we measure on stable DOM, not on every token.
 *
 * Security: the input HTML is sanitized with DOMPurify before any DOM
 * injection. AI-generated drafts go through this path so the renderer is
 * the trust boundary.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { APP } from './tokens';
import { getPageDims, type PageSize } from './pageConstants';

interface PaginatedDocumentProps {
  html: string;
  /** When true, debounce repagination — content is still arriving. */
  streaming?: boolean;
  pageSize?: PageSize;
  /** Visual zoom (1.0 = 100%). Doesn't change page geometry; just scales. */
  zoom?: number;
  /** Mounting id for the wrapper — needed so the print stylesheet can target it. */
  id?: string;
  /**
   * Optional. When provided, selecting text inside the rendered draft pops a
   * floating "Ask Corner" button. Calling it forwards the selected snippet to
   * the chat composer.
   */
  onAskCornerAboutSelection?: (snippet: { text: string; page?: number }) => void;
}

const STREAM_DEBOUNCE_MS = 80;
const DOC_PAGE_BG = '#FFFFFF';
const DOC_PAGE_BORDER = '#E0DCD4';
const DOC_TEXT = '#1A1714';
const DOC_MUTED_TEXT = '#6B5E52';
const DOC_CODE_BG = '#F5F2EC';

// Re-paginate at most this often when content is streaming. Beyond this, drop
// updates and let the next debounce coalesce them. Prevents a long-running
// paginate pass from blocking the next delta render.
const MIN_PAGINATE_INTERVAL_MS = 60;

// Typed via the call signature so we don't pull in the (non-namespace)
// internal DOMPurify type — DOMPurify v3 ships its types differently across
// versions and the namespace form is not always available.
const SANITIZE_CONFIG: Parameters<typeof DOMPurify.sanitize>[1] = {
  ALLOWED_TAGS: [
    'p', 'br', 'span',
    'strong', 'b', 'em', 'i', 'u', 's', 'mark', 'small', 'sub', 'sup',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'dl', 'dt', 'dd',
    'blockquote', 'hr',
    'a',
    'code', 'pre',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'img', 'figure', 'figcaption',
    'div',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel',
    'src', 'alt', 'title',
    'colspan', 'rowspan',
    'class', 'style',
    'data-corner-page-break',
  ],
};

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, SANITIZE_CONFIG) as unknown as string;
}

export default function PaginatedDocument({
  html,
  streaming = false,
  pageSize = 'letter',
  zoom = 1,
  id = 'corner-paginated-doc',
  onAskCornerAboutSelection,
}: PaginatedDocumentProps) {
  const dims = useMemo(() => getPageDims(pageSize), [pageSize]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [selectionToolbar, setSelectionToolbar] = useState<{
    left: number;
    top: number;
    text: string;
    page?: number;
  } | null>(null);

  // Each entry is the inner HTML for one page. Sanitized before storage,
  // then assigned to a ref'd container without ever passing it through
  // dangerouslySetInnerHTML — see effect below.
  const [pageHtml, setPageHtml] = useState<string[]>(() => ['']);
  const pageNodeRefs = useRef<(HTMLDivElement | null)[]>([]);

  const pendingRunRef = useRef<number | null>(null);
  const lastRunAtRef = useRef<number>(0);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const updateWidth = () => setAvailableWidth(wrap.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const measure = measureRef.current;
    if (!measure) return;

    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      const now = performance.now();
      if (streaming && now - lastRunAtRef.current < MIN_PAGINATE_INTERVAL_MS) {
        if (pendingRunRef.current != null) window.clearTimeout(pendingRunRef.current);
        pendingRunRef.current = window.setTimeout(run, MIN_PAGINATE_INTERVAL_MS);
        return;
      }
      lastRunAtRef.current = now;
      const next = paginateHtml(html, dims, measure);
      setPageHtml(next.length > 0 ? next : ['']);
    };

    if (streaming) {
      if (pendingRunRef.current != null) window.clearTimeout(pendingRunRef.current);
      pendingRunRef.current = window.setTimeout(run, STREAM_DEBOUNCE_MS);
    } else {
      run();
    }

    return () => {
      cancelled = true;
      if (pendingRunRef.current != null) {
        window.clearTimeout(pendingRunRef.current);
        pendingRunRef.current = null;
      }
    };
  }, [html, streaming, dims]);

  // Re-paginate on viewport resize — line wrapping can change when fonts load
  // or the surrounding layout shifts.
  useEffect(() => {
    const onResize = () => {
      const measure = measureRef.current;
      if (!measure) return;
      setPageHtml(paginateHtml(html, dims, measure));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [html, dims]);

  // Apply already-sanitized inner HTML imperatively. Avoids React's
  // dangerouslySetInnerHTML path entirely and keeps the trust boundary in
  // one place (paginateHtml -> sanitize).
  useEffect(() => {
    pageNodeRefs.current.forEach((node, i) => {
      if (!node) return;
      const next = pageHtml[i] ?? '';
      // innerHTML is the standard browser API; the source string came out of
      // DOMPurify in paginateHtml so it's already script-safe.
      node.innerHTML = next;
    });
  }, [pageHtml]);

  const pageStackHeight = pageHtml.length * dims.height + Math.max(0, pageHtml.length - 1) * dims.gap;
  const fitScale = availableWidth > 0 ? Math.min(1, availableWidth / dims.width) : 1;
  const effectiveScale = Math.min(zoom, fitScale);

  // Selection → "Ask Corner" floating button.
  // - Skipped while content is still streaming so the toolbar doesn't pop up
  //   on transient pagination jitter.
  // - Position is computed against `wrapRef` (the same container the toolbar
  //   is positioned inside), and `getBoundingClientRect` already accounts for
  //   the page-stack `transform: scale(...)`, so we don't need to undo it.
  const updateSelectionToolbar = useCallback(() => {
    if (!onAskCornerAboutSelection || streaming) {
      setSelectionToolbar(null);
      return;
    }
    const wrap = wrapRef.current;
    const container = containerRef.current;
    if (!wrap || !container) return;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
      setSelectionToolbar(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
      setSelectionToolbar(null);
      return;
    }
    const text = sel.toString().replace(/\s+/g, ' ').trim();
    if (!text) {
      setSelectionToolbar(null);
      return;
    }
    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      setSelectionToolbar(null);
      return;
    }
    const wrapRect = wrap.getBoundingClientRect();
    const left = rect.left + rect.width / 2 - wrapRect.left;
    const top = rect.top - wrapRect.top - 8;

    let page: number | undefined;
    const startEl =
      range.startContainer instanceof HTMLElement
        ? range.startContainer
        : range.startContainer.parentElement;
    const pageEl = startEl?.closest('.corner-page');
    if (pageEl?.parentElement) {
      const idx = Array.from(pageEl.parentElement.children).indexOf(pageEl);
      if (idx >= 0) page = idx + 1;
    }

    setSelectionToolbar({ left, top, text, page });
  }, [onAskCornerAboutSelection, streaming]);

  useEffect(() => {
    if (!onAskCornerAboutSelection) return;
    document.addEventListener('selectionchange', updateSelectionToolbar);
    return () => document.removeEventListener('selectionchange', updateSelectionToolbar);
  }, [onAskCornerAboutSelection, updateSelectionToolbar]);

  // Dismiss the toolbar on Escape and on outside mousedown so it doesn't
  // linger when the user clicks somewhere else.
  useEffect(() => {
    if (!selectionToolbar) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectionToolbar(null);
    };
    const onDown = (e: MouseEvent) => {
      const btn = document.getElementById('paginated-doc-ask-corner-btn');
      if (btn && btn.contains(e.target as Node)) return;
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [selectionToolbar]);

  const handleAskCorner = useCallback(() => {
    if (!selectionToolbar || !onAskCornerAboutSelection) return;
    onAskCornerAboutSelection({ text: selectionToolbar.text, page: selectionToolbar.page });
    setSelectionToolbar(null);
    window.getSelection()?.removeAllRanges();
  }, [selectionToolbar, onAskCornerAboutSelection]);

  return (
    <div ref={wrapRef} style={WRAP_STYLE}>
      <div
        style={{
          width: dims.width * effectiveScale,
          height: pageStackHeight * effectiveScale,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <div
          id={id}
          ref={containerRef}
          className="corner-paginated-doc"
        style={{
          position: 'absolute',
          inset: 0,
          width: dims.width,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: dims.gap,
          transform: effectiveScale !== 1 ? `scale(${effectiveScale})` : undefined,
          transformOrigin: 'top center',
        }}
      >
        {pageHtml.map((_, i) => (
          <div
            key={i}
            className="corner-page"
            style={{
              width: dims.width,
              height: dims.height,
              padding: `${dims.padY}px ${dims.padX}px`,
              boxSizing: 'border-box',
              backgroundColor: DOC_PAGE_BG,
              borderRadius: 8,
              border: `1px solid ${DOC_PAGE_BORDER}`,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <div
              ref={(node) => {
                pageNodeRefs.current[i] = node;
              }}
              className="corner-page-content"
              style={{ width: '100%', height: '100%', overflow: 'hidden' }}
            />
          </div>
        ))}
        </div>
      </div>

      {/* Hidden measurement container — same width and prose styling as a real page. */}
      <div
        ref={measureRef}
        aria-hidden
        className="corner-page-measure"
        style={{
          position: 'fixed',
          left: -99999,
          top: 0,
          width: dims.width - dims.padX * 2,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      />

      {selectionToolbar && onAskCornerAboutSelection && (
        <div
          style={{
            position: 'absolute',
            left: selectionToolbar.left,
            top: selectionToolbar.top,
            transform: 'translate(-50%, -100%)',
            zIndex: 60,
            pointerEvents: 'none',
          }}
        >
          <button
            id="paginated-doc-ask-corner-btn"
            type="button"
            // preventDefault on mousedown so the click doesn't collapse the
            // selection before the click handler fires.
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleAskCorner}
            style={{
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 12px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: '#D97645',
              color: '#FDFCFB',
              fontFamily: APP.fontFamily,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              whiteSpace: 'nowrap',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"
                fill="#FDFCFB"
              />
            </svg>
            Ask Corner
          </button>
        </div>
      )}

      <style>{paginatedDocCss(id, dims)}</style>
    </div>
  );
}

const WRAP_STYLE: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  // Anchor for the absolutely-positioned selection toolbar.
  position: 'relative',
};

// ─── Pagination algorithm ──────────────────────────────────────────────────

/**
 * Returns one HTML string per visual page. Each returned string is already
 * sanitized via DOMPurify (AI-generated content is the input).
 *
 * The measurement element must be styled identically to a `.corner-page-content`
 * (same width and prose CSS) so heights match what the live page will render.
 */
function paginateHtml(
  html: string,
  dims: { width: number; height: number; padX: number; padY: number },
  measure: HTMLElement,
): string[] {
  const cleaned = sanitize(stripFences(html).trim() || '<p></p>');

  const innerWidth = dims.width - dims.padX * 2;
  measure.style.width = `${innerWidth}px`;
  const contentHeight = dims.height - dims.padY * 2;

  const doc = new DOMParser().parseFromString(cleaned, 'text/html');
  const queue: Element[] = collectAtoms(doc.body);

  const pages: string[] = [];
  let current: Element[] = [];

  const renderInto = (target: HTMLElement, els: Element[]) => {
    target.innerHTML = '';
    for (const el of els) target.appendChild(el.cloneNode(true));
  };

  const heightWith = (extra?: Element): number => {
    if (extra) {
      renderInto(measure, [...current, extra]);
    } else {
      renderInto(measure, current);
    }
    return measure.scrollHeight;
  };

  const flushPage = () => {
    pages.push(current.map(el => (el as HTMLElement).outerHTML).join(''));
    current = [];
  };

  while (queue.length > 0) {
    const atom = queue.shift()!;

    if (atom instanceof HTMLElement && atom.matches('[data-corner-page-break]')) {
      if (current.length > 0) flushPage();
      // If a hard break lands at the top of a measured page, emitting an empty
      // page creates the visible "blank page then continue" artifact. Treat
      // leading/consecutive breaks as no-ops; there is no current content to
      // advance from.
      continue;
    }

    const withAtomH = heightWith(atom);
    if (withAtomH <= contentHeight) {
      const nextAtom = queue[0];
      if (
        current.length > 0
        && isHeading(atom)
        && nextAtom
        && !(nextAtom instanceof HTMLElement && nextAtom.matches('[data-corner-page-break]'))
      ) {
        renderInto(measure, [...current, atom, nextAtom]);
        const withNextOnCurrentPage = measure.scrollHeight;
        renderInto(measure, [atom, nextAtom]);
        const pairFitsFreshPage = measure.scrollHeight <= contentHeight;
        if (withNextOnCurrentPage > contentHeight && pairFitsFreshPage) {
          flushPage();
          queue.unshift(atom);
          continue;
        }
      }
      current.push(atom);
      continue;
    }

    if (current.length === 0) {
      // Atom alone overflows the page.
      if (isSplittable(atom)) {
        const split = splitElementToFit(atom, contentHeight, measure, [], contentHeight);
        if (split.head) {
          current.push(split.head);
          flushPage();
        }
        if (split.tail) {
          queue.unshift(split.tail);
        } else if (!split.head) {
          // Couldn't extract any prefix — accept overflow.
          current.push(atom);
          flushPage();
        }
      } else {
        // Atomic and oversize. Don't drop content; let it overflow its page.
        current.push(atom);
        flushPage();
      }
      continue;
    }

    if (isSplittable(atom)) {
      const usedH = heightWith();
      const remaining = contentHeight - usedH;
      const split = splitElementToFit(atom, remaining, measure, current, contentHeight);
      if (split.head) current.push(split.head);
      flushPage();
      if (split.tail) queue.unshift(split.tail);
    } else {
      flushPage();
      queue.unshift(atom);
    }
  }

  if (current.length > 0) flushPage();
  if (pages.length === 0) pages.push('');
  return pages;
}

function stripFences(html: string): string {
  let s = html.replace(/^\uFEFF/, '');
  if (/^```(?:html)?\s*/i.test(s)) s = s.replace(/^```(?:html)?\s*/i, '');
  s = s.replace(/\n?```\s*$/, '');
  return s;
}

/**
 * Splits the body into a flat list of pagination atoms. Mirrors the logic in
 * RichTextEditor.collectFlowUnits so the unit boundaries are consistent: list
 * items, def-list rows, and table rows are individually flowable so very long
 * lists don't get stuck on a single page.
 */
function collectAtoms(root: HTMLElement): Element[] {
  const out: Element[] = [];

  const visit = (block: Element) => {
    if (!(block instanceof HTMLElement)) {
      out.push(block);
      return;
    }
    if (isGenericWrapperDiv(block)) {
      Array.from(block.children).forEach(visit);
      return;
    }
    if (block.matches('[data-corner-page-break]')) {
      out.push(block);
      return;
    }
    if (block.matches('ol, ul')) {
      const items = Array.from(block.querySelectorAll(':scope > li'));
      if (items.length === 0) {
        out.push(block);
        return;
      }
      // Wrap each <li> in its own list element so list semantics survive
      // splitting across pages.
      for (const li of items) {
        const wrap = block.cloneNode(false) as HTMLElement;
        wrap.appendChild(li.cloneNode(true));
        out.push(wrap);
      }
      return;
    }
    if (block.matches('dl')) {
      const rows = Array.from(block.querySelectorAll(':scope > dt, :scope > dd'));
      if (rows.length === 0) {
        out.push(block);
        return;
      }
      for (const row of rows) {
        const wrap = block.cloneNode(false) as HTMLElement;
        wrap.appendChild(row.cloneNode(true));
        out.push(wrap);
      }
      return;
    }
    out.push(block);
  };

  Array.from(root.children).forEach(visit);
  return out;
}

const BLOCKISH = /^(H1|H2|H3|H4|H5|H6|P|OL|UL|BLOCKQUOTE|TABLE|HR|DIV|DL|PRE|FIGURE)$/;

function isGenericWrapperDiv(el: HTMLElement): boolean {
  if (el.tagName !== 'DIV') return false;
  if (el.matches('[data-corner-page-break]')) return false;
  if (el.getAttribute('data-type')) return false;
  if (el.getAttribute('role')) return false;
  if (el.className && String(el.className).trim()) return false;
  const children = Array.from(el.children) as HTMLElement[];
  return children.length > 0 && children.some(c => BLOCKISH.test(c.tagName));
}

/**
 * Paragraphs and blockquotes can be split. Headings, lists, tables, code,
 * images stay atomic. Type guard so TS narrows `Element` -> `HTMLElement`
 * at the call site.
 */
function isSplittable(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  return el.matches('p, blockquote');
}

function isHeading(el: Element): el is HTMLElement {
  return el instanceof HTMLElement && el.matches('h1, h2, h3, h4, h5, h6');
}

interface SplitResult {
  head: HTMLElement | null;
  tail: HTMLElement | null;
}

/**
 * Returns [head, tail] where `head` fits in `remainingHeight` (when appended
 * after `existing` content on the page) and `tail` carries the rest.
 *
 * Strategy: candidate cut indices at sentence boundaries first, then word
 * boundaries, then newlines. Pick the largest cut that still fits.
 */
function splitElementToFit(
  source: HTMLElement,
  remainingHeight: number,
  measure: HTMLElement,
  existing: Element[],
  fullContentHeight: number,
): SplitResult {
  const text = source.textContent ?? '';
  if (!text.trim()) return { head: null, tail: null };

  const candidates = buildCutCandidates(text);
  if (candidates.length === 0) return { head: null, tail: null };

  const fits = (offset: number): HTMLElement | null => {
    const head = sliceElementAtTextOffset(source, offset);
    if (!head) return null;
    measure.innerHTML = '';
    for (const el of existing) measure.appendChild(el.cloneNode(true));
    measure.appendChild(head);
    const totalH = measure.scrollHeight;
    if (totalH > fullContentHeight) return null;
    const baseH = sumExistingHeight(measure, existing.length);
    if ((totalH - baseH) > remainingHeight) return null;
    return head;
  };

  // Largest first — keep as much on the current page as possible.
  for (let i = candidates.length - 1; i >= 0; i--) {
    const head = fits(candidates[i]);
    if (head) {
      const tail = sliceElementFromTextOffset(source, candidates[i]);
      return { head, tail };
    }
  }

  return { head: null, tail: null };
}

function sumExistingHeight(measure: HTMLElement, existingCount: number): number {
  if (existingCount === 0) return 0;
  const last = measure.children[existingCount - 1] as HTMLElement | undefined;
  if (!last) return 0;
  return last.offsetTop + last.offsetHeight;
}

/** Sentence-ends, word-ends, and newlines, in ascending order, deduped. */
function buildCutCandidates(text: string): number[] {
  const set = new Set<number>();
  // Sentence ends (. ! ?) followed by whitespace or end.
  for (const m of text.matchAll(/[.!?](?=\s|$)/g)) {
    if (m.index != null) set.add(m.index + 1);
  }
  // Word ends — first whitespace after a non-whitespace.
  for (const m of text.matchAll(/\S\s+/g)) {
    if (m.index != null) set.add(m.index + 1);
  }
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n') set.add(i + 1);
  }
  return Array.from(set).sort((a, b) => a - b);
}

function sliceElementAtTextOffset(source: HTMLElement, offset: number): HTMLElement | null {
  if (offset <= 0) return null;
  const clone = source.cloneNode(true) as HTMLElement;
  const consumed = pruneAfterTextOffset(clone, offset);
  if (consumed <= 0) return null;
  return clone;
}

function sliceElementFromTextOffset(source: HTMLElement, offset: number): HTMLElement | null {
  const clone = source.cloneNode(true) as HTMLElement;
  const consumed = pruneBeforeTextOffset(clone, offset);
  if (consumed <= 0) return null;
  if (!(clone.textContent ?? '').length) return null;
  return clone;
}

/**
 * Walks `el` depth-first and removes every text-content character past
 * `offset`, keeping the inline tag nesting around the kept text intact.
 */
function pruneAfterTextOffset(el: HTMLElement, offset: number): number {
  let remaining = offset;
  const walk = (node: Node): boolean => {
    if (remaining <= 0) {
      node.parentNode?.removeChild(node);
      return true;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node as Text).data;
      if (text.length <= remaining) {
        remaining -= text.length;
        return false;
      }
      (node as Text).data = text.slice(0, remaining);
      remaining = 0;
      return true;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const children = Array.from(node.childNodes);
      for (const child of children) {
        const stop = walk(child);
        if (stop && remaining <= 0) {
          const idx = children.indexOf(child);
          for (let i = idx + 1; i < children.length; i++) {
            children[i].parentNode?.removeChild(children[i]);
          }
          return true;
        }
      }
      return false;
    }
    return false;
  };
  walk(el);
  return offset - remaining;
}

function pruneBeforeTextOffset(el: HTMLElement, offset: number): number {
  let toDrop = offset;
  const walk = (node: Node): boolean => {
    if (toDrop <= 0) return true;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node as Text).data;
      if (text.length <= toDrop) {
        toDrop -= text.length;
        node.parentNode?.removeChild(node);
        return false;
      }
      (node as Text).data = text.slice(toDrop);
      toDrop = 0;
      return true;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const children = Array.from(node.childNodes);
      for (const child of children) {
        const done = walk(child);
        if (done) return true;
      }
      if (!node.childNodes.length && node !== el && (node as Element).tagName) {
        const inline = /^(SPAN|STRONG|EM|B|I|U|A|CODE|MARK|SUB|SUP|SMALL)$/i.test((node as Element).tagName);
        if (inline) node.parentNode?.removeChild(node);
      }
      return false;
    }
    return false;
  };
  walk(el);
  return offset - toDrop;
}

// ─── CSS ────────────────────────────────────────────────────────────────────

function paginatedDocCss(rootId: string, dims: { width: number; padX: number }) {
  const innerWidth = dims.width - dims.padX * 2;
  // Identical prose styling to the editor (kept in sync manually — both come
  // from the same design tokens). If the editor's CSS changes, mirror here.
  return `
.corner-page-content,
.corner-page-measure {
  font-family: var(--chat-font-family, ${APP.fontFamily});
  font-size: 11pt;
  line-height: 1.15;
  color: ${DOC_TEXT};
  width: ${innerWidth}px;
  box-sizing: border-box;
}
.corner-page-content h1, .corner-page-measure h1 { font-size: 18pt; font-weight: 700; line-height: 1.25; margin: 0 0 12pt; color: ${DOC_TEXT}; }
.corner-page-content h2, .corner-page-measure h2 { font-size: 14pt; font-weight: 600; line-height: 1.3;  margin: 18pt 0 10pt; color: ${DOC_TEXT}; }
.corner-page-content h3, .corner-page-measure h3 { font-size: 12pt; font-weight: 600; line-height: 1.35; margin: 14pt 0 8pt;  color: ${DOC_TEXT}; }
.corner-page-content p,  .corner-page-measure p  { margin: 0 0 10pt; }
.corner-page-content ul, .corner-page-measure ul,
.corner-page-content ol, .corner-page-measure ol { padding-left: 24px; margin: 0 0 10pt; }
.corner-page-content li, .corner-page-measure li { margin-bottom: 3pt; }
.corner-page-content blockquote, .corner-page-measure blockquote {
  border-left: 3px solid ${DOC_PAGE_BORDER};
  padding-left: 14pt; margin: 12pt 0;
  color: ${DOC_MUTED_TEXT}; font-style: italic;
}
.corner-page-content hr, .corner-page-measure hr {
  border: none; border-top: 1px solid ${DOC_PAGE_BORDER}; margin: 18pt 0;
}
.corner-page-content code, .corner-page-measure code {
  background-color: ${DOC_CODE_BG};
  padding: 2px 5px; border-radius: 3px; font-size: 10pt;
}
.corner-page-content pre, .corner-page-measure pre {
  white-space: pre-wrap; word-break: break-word;
  background-color: ${DOC_CODE_BG};
  padding: 8pt; border-radius: 4px; margin: 10pt 0; font-size: 10pt;
}
.corner-page-content img, .corner-page-measure img { max-width: 100%; height: auto; }
.corner-page-content [data-corner-page-break],
.corner-page-measure  [data-corner-page-break] { display: none; }

@media print {
  /* Let the print engine flow content naturally instead of clipping to the
     on-screen viewport scroll position. */
  html, body {
    overflow: visible !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    background: white !important;
  }
  /* Hide all app chrome. The doc subtree is re-shown below. */
  body * { visibility: hidden; }
  #${rootId}, #${rootId} * { visibility: visible; }
  /* Detach the doc from any ancestor positioning/scale so pages render at
     their nominal letter size rather than the on-screen zoomed/scaled size. */
  #${rootId} {
    position: static !important;
    transform: none !important;
    gap: 0 !important;
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
  }
  /* Force each page to its physical letter dimensions and break cleanly. */
  .corner-page {
    box-shadow: none !important;
    border: 0 !important;
    border-radius: 0 !important;
    margin: 0 !important;
    page-break-after: always;
    break-after: page;
  }
  .corner-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  /* The hidden measurement container can't be allowed to leak any space on
     paper — Chrome sometimes counts off-screen blocks toward page count. */
  .corner-page-measure { display: none !important; }
}
@page { size: letter; margin: 0; }
`;
}
